import {
  NfpDocumentSubmitPayload,
  SaveVendorAssessmentPayload,
  type ExistingSite,
} from "@/types/VendorService.types";
import api from "./api";
import { normalizeVendorAssessmentInResponse } from "@/utils/vendor/normalizeVendorAssessmentResponse";
import { normalizeExistingSitesFromApi } from "@/utils/vendor/siteApiFieldUtils";

const { VendorGuestServiceAPI, pimcoreGuestServiceAPI } = api;

const GET_GUEST_VENDOR_PATH = "/vendor/assessment/getVendor";
const GET_GUEST_EXISTING_FACILITIES_PATH = "/vendor/assessment/getExistingFacilities";
const GET_GUEST_VENDOR_ON_LOAD_PATH = "/vendor/assessment/form/onload";
const GET_GUEST_VENDOR_ASSESSMENT_SAVE_PATH = "/vendor/assessment/save";
const GET_GUEST_ASSESSMENT_FORM_DETAILS_PATH = "/vendor/assessment/form/";
const GET_GUEST_ASSESSMENT_FORM_DETAILS_CONSOLIDATED_PATH = "/vendor/assessment/";
const GET_GUEST_INGREDIENT_GUIDE_DATA_PATH = "/vendor/assessment/ingredient/guide";
const GET_GUEST_REQUIRED_DOCUMENTS_PATH = "/vendor/assessment/required/documents";
const GET_GUEST_VENDOR_ASSESSMENT_CONDACT_INFO_PATH ="/vendor/assessment/contactInfo/";
const GUEST_ASSESSMENT_INGREDIENTS_DETAILS_PATH ="/vendor/assessment/ingredients/";
const GET_VENDOR_MANUFACTURING_FACILITY_INFORMATION_PATH ="/vendor/assessment/manufacturingfacility";
const GUEST_VENDOR_SHARE_FOR_REVIEW_PATH = "/vendor/assessment/share/review/";
const GET_GUEST_VENDOR_TASKS_PATH = "/vendor/assessment/task";
const GET_GUEST_TASK_GROUP_COUNT_PATH = "/task/groupCount/generic";
const GET_GUEST_TASK_GENERIC_PATH = "/task/generic";
const SUBMIT_NFP_DOCUMENT_PATH = "/vendor/assessment/request_nfp/";

// In-flight promise so concurrent callers (e.g. multiple components) get one API call
let groupCountRequest: Promise<unknown> | null = null;

function normalizeGuestExistingFacilitiesToSites(data: unknown): ExistingSite[] {
  return normalizeExistingSitesFromApi(data);
}

const VendorGuestService = {

  /**
   * When the API returns `vendorAssessment` as an array, pass the assessment id from the guest URL
   * so the correct entry (e.g. `hasExistingVendor`) is selected instead of always the first item.
   */
  getGuestVendor: async (vendorAssessmentIdHint?: string | null) => {
    try {
      const resp = await VendorGuestServiceAPI.get(GET_GUEST_VENDOR_PATH);
      const trimmed = vendorAssessmentIdHint?.trim() ?? "";
      return normalizeVendorAssessmentInResponse(
        resp.data,
        trimmed !== "" ? trimmed : undefined
      );
    } catch (error) {
      throw error;
    }
  },

  /**
   * Guest: existing manufacturing sites for Site Information when vendorAssessment.hasExistingVendor.
   * Optional vendorAssessmentId for multi-assessment guest links.
   */
  getGuestExistingFacilities: async (
    vendorAssessmentId?: string | null
  ): Promise<ExistingSite[]> => {
    try {
      const trimmed = vendorAssessmentId?.trim() ?? "";
      const path = trimmed
        ? `${GET_GUEST_EXISTING_FACILITIES_PATH}?vendorAssessmentId=${encodeURIComponent(trimmed)}`
        : GET_GUEST_EXISTING_FACILITIES_PATH;
      const resp = await VendorGuestServiceAPI.get(path);
      return normalizeGuestExistingFacilitiesToSites(resp?.data);
    } catch (error) {
      throw error;
    }
  },
  getGuestVendorOnLoadData: async () => {
    try {
      const resp = await VendorGuestServiceAPI.get(
        GET_GUEST_VENDOR_ON_LOAD_PATH
      );
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
  saveGuestVendorAssessment: async (
    SaveGuestVendorAssessmentPayload: SaveVendorAssessmentPayload
  ) => {
    try {
      const resp = await VendorGuestServiceAPI.post(
        GET_GUEST_VENDOR_ASSESSMENT_SAVE_PATH,
        SaveGuestVendorAssessmentPayload
      );
      return resp;
    } catch (error) {
      throw error;
    }
  },
  getGuestAssessmentFormDetails: async (id: string) => {
    try {
      const resp = await VendorGuestServiceAPI.get(
        GET_GUEST_ASSESSMENT_FORM_DETAILS_PATH + id
      );
      return resp;
    } catch (error) {
      throw error;
    }
  },
  getGuestAssessmentFormDetailsConsolidated: async (assessmentId: string) => {
    try {
      const resp = await VendorGuestServiceAPI.get(
        GET_GUEST_ASSESSMENT_FORM_DETAILS_CONSOLIDATED_PATH + assessmentId
      );
      return resp;
    } catch (error) {
      throw error;
    }
  },
  getGuestIngredientGuideData: async () => {
    try {
      const resp = await VendorGuestServiceAPI.get(
        GET_GUEST_INGREDIENT_GUIDE_DATA_PATH
      );
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
  getGuestRequiredDocuments: async () => {
    try {
      const resp = await VendorGuestServiceAPI.get(
        GET_GUEST_REQUIRED_DOCUMENTS_PATH
      );
      return resp;
    } catch (error) {
      throw error;
    }
  },
  getGuestVendorAssessmentContactInfo: async (id: string) => {
    try {
      const resp = await VendorGuestServiceAPI.get(
        GET_GUEST_VENDOR_ASSESSMENT_CONDACT_INFO_PATH + id
      );
      return resp;
    } catch (error) {
      throw error;
    }
  },
  getGuestAssessmentIngredientsDetails: async (id: string) => {
    try {
      const resp = await VendorGuestServiceAPI.get(
        GUEST_ASSESSMENT_INGREDIENTS_DETAILS_PATH + id
      );
      return resp;
    } catch (error) {
      throw error;
    }
  },
  getGuestVendorManufacturingFacilityInformation: async (manufacturingFacilityId: string[]) => {
    try {
      const resp = await VendorGuestServiceAPI.post(
        GET_VENDOR_MANUFACTURING_FACILITY_INFORMATION_PATH,manufacturingFacilityId
      );
      return resp;
    } catch (error) {
      throw error;
    }
  },
  guestVendorShareForReview: async (taskId:string,payload:any) => {
    try {
      const resp = await VendorGuestServiceAPI.post(
        GUEST_VENDOR_SHARE_FOR_REVIEW_PATH+taskId,
        payload
      );
      console.log("share for review resp:", resp)
      return resp.data; 
    } catch (error) {
      throw error; 
    }
  },
  /**
   * Fetch task group counts using new generic API for guest vendors
   * Returns grouped task counts by category.
   * Dedupes concurrent calls so only one request is made.
   */
  fetchVendorTaskGroupCounts: async () => {
    if (groupCountRequest) return groupCountRequest;
    groupCountRequest = (async () => {
      try {
        const resp = await pimcoreGuestServiceAPI.get(GET_GUEST_TASK_GROUP_COUNT_PATH);
        return resp.data;
      } finally {
        groupCountRequest = null;
      }
    })();
    return groupCountRequest;
  },
  /**
   * Fetch tasks using new generic API with pagination and group filtering for guest vendors
   * @param options - Pagination and filtering options
   */
  fetchVendorTasksGeneric: async (options: { 
    pageNo?: number; 
    pageSize?: number; 
    groupName?: string;
    assigneeUserId?: string[];
    assigneeRoleId?: string[];
    creationDate?: {
      startDate?: string;
      endDate?: string;
    };
    search?: string;
  } = {}) => {
    try {
      const payload: any = {};
      
      if (options.pageNo !== undefined) {
        payload.pageNo = options.pageNo;
      }
      if (options.pageSize !== undefined) {
        payload.pageSize = options.pageSize;
      }
      if (options.groupName) {
        payload.groupName = options.groupName;
      }
      if (options.assigneeUserId && options.assigneeUserId.length > 0) {
        payload.assigneeUserId = options.assigneeUserId;
      }
      if (options.assigneeRoleId && options.assigneeRoleId.length > 0) {
        payload.assigneeRoleId = options.assigneeRoleId;
      }
      if (options.creationDate) {
        payload.creationDate = options.creationDate;
      }
      if (options.search) {
        payload.search = options.search;
      }
      
      const resp = await pimcoreGuestServiceAPI.post(GET_GUEST_TASK_GENERIC_PATH, payload);
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
  /**
   * Fetch vendor tasks (legacy method)
   * @deprecated Use fetchVendorTasksGeneric for new implementations
   */
  fetchVendorTasks: async () => {
    try {
      const resp = await VendorGuestServiceAPI.get(GET_GUEST_VENDOR_TASKS_PATH); 
      return resp.data; 
    } catch (error) {
      throw error; 
    }
  },
  submitNfpDocument: async (id: string, payload: NfpDocumentSubmitPayload) => {
    try {
      const resp = await VendorGuestServiceAPI.put(SUBMIT_NFP_DOCUMENT_PATH + id, payload);
      return resp;
    } catch (error) {
      throw error;
    }
  }
};

export default VendorGuestService;